import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { 
  createProject, 
  getProject, 
  updateProject, 
  uploadAttachment, 
  getAttachments,
  updateAttachment,
  deleteAttachment, 
  uploadGutterAttachment, 
  getGutterAttachments as getGutterAttachmentsApi, 
  deleteGutterAttachment,
  uploadSupplementAttachment,
  getSupplementAttachments as getSupplementAttachmentsApi,
} from '../api';

interface Project {
  buildDate?: Date | null | undefined;
  buildNotes?: string
  
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  
  address: string;
  manager: string;

  house: boolean;
  garage: boolean;
  shed: boolean;
  flat: boolean;
  reDeck: boolean;
  twoStory: boolean;
  removeGutters: boolean;
  layers?: number | '';
  roofPitch: string;
  roofingType: string;
  notes: string;

  status: string;
  jobCosts: any[];
  materials: any[];
  supplements: any[];
  attachments: any[];
  gutters: any;
  estimate: any;
  invoice: any;

  dropDate: any;
  dropLocation: any;
  deliveryDetails: any;
  customer: any;
  city: string;
  state: string;
  zip: string;

  archived?: boolean;
  deleted?: boolean;

  settings: any;
}

interface ProjectContextType {
  project: Project | null | undefined;
  set: (project: Project) => void;
  get: () => Promise<Project | null | undefined>,
  refresh: () => Promise<void>,
  create: (project: Project) => Promise<Project>;
  update: (project: Project) => Promise<void>;

  addAttachment(data: any): Promise<void>;
  getProjectAttachments(): Promise<any[]>;
  updateProjectAttachment: (data: any) => Promise<any>;
  removeProjectAttachment(data: any): Promise<void>;

  addGutterAttachment(data: any): Promise<void>;
  getGutterAttachments(): Promise<any[]>;
  removeGutterAttachment(data: any): Promise<void>;

  addSupplementAttachment(data: any): Promise<void>;
  getSupplementAttachments(supplementId: string): Promise<any[]>;
  removeSupplementAttachment(data: any): Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();
  const params = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null | undefined>(null);

  const set = useCallback((project: Project) => {
    setProject(project);
  }, []);

  const get = useCallback(async () => {
    const projectId = params.projectId;
    if(!projectId) return;

    if(!project) {
      try {
        const project = await getProject(projectId);
        setProject(project);
      }catch(e) {
        setProject(undefined);
      }
    }

    return project;
  }, []);

  const refresh = useCallback(async () => {
    const projectId = params.projectId;
    if(!projectId) return;

    try {
      const p = await getProject(projectId);
      console.log('REFRESH', p);
      setProject(p);
    }catch(e) {
      setProject(undefined);
    }
  }, []);

  const create = useCallback(async (project: Project) => {
    const newProject = await createProject(project);
    setProject(newProject);

    enqueueSnackbar('Project created successfully', 
    { 
      variant: 'success',
      autoHideDuration: 3000,
      anchorOrigin: { horizontal: 'center', vertical: 'bottom' }
    });
    
    return newProject;
  }, []);

  const update = useCallback(async (project: Project) => {
    setProject(await updateProject(project))
    enqueueSnackbar('Project updated successfully', 
      { 
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' }
      });
  }, []);

  const addAttachment = useCallback(async (data: any) => {
    if(!params.projectId) return;
    const result =  await uploadAttachment({projectId: params.projectId, ...data})
    return result;
  }, []);

  const getProjectAttachments = useCallback(async () => {
    if(!params.projectId) return;
    const attachments = await getAttachments(params.projectId);
    return attachments;
  }, []);

  const removeProjectAttachment = useCallback(async (data: any) => {
    if(!params.projectId) return;
    const { attachmentId } = data;
    await deleteAttachment({projectId: params.projectId, attachmentId})
  }, []);

  const addGutterAttachment = useCallback(async (data: any) => {
    if(!params.projectId) return;
    const result = uploadGutterAttachment({projectId: params.projectId, ...data})
    return result
  }, []);

  const getGutterAttachments = useCallback(async () => {
    if(!params.projectId) return;
    const attachments = await getGutterAttachmentsApi(params.projectId);
    return attachments;
  }, []);

  const updateProjectAttachment = useCallback(async (data: any) => {
    return await updateAttachment(data)
  }, []);

  const removeGutterAttachment = useCallback(async (data: any) => {
    if(!params.projectId) return;
    const { attachmentId } = data;
    await deleteGutterAttachment({projectId: params.projectId, attachmentId})
  }, []);

  const addSupplementAttachment = useCallback(async (data: any) => {
    if(!params.projectId || !data.supplementId) return;
    const result = await uploadSupplementAttachment({projectId: params.projectId, ...data})
    return result
  }, []);

  const getSupplementAttachments = useCallback(async (supplementId: string) => {
    if(!params.projectId) return;
    const attachments = await getSupplementAttachmentsApi({projectId: params.projectId, supplementId});
    return attachments;
  }, []);

  const removeSupplementAttachment = useCallback(async (data: any) => {
    if(!params.projectId) return;
    const { supplementId, attachmentId } = data;
    await deleteGutterAttachment({projectId: params.projectId, supplementId, attachmentId})
  }, []);


  useEffect(() => {
    get();
  }, []);

  const value = {
    project,
    set,
    get,
    refresh,
    create,
    update,
    addAttachment,
    getProjectAttachments,
    updateProjectAttachment,
    removeProjectAttachment,
    addGutterAttachment,
    getGutterAttachments,
    removeGutterAttachment,
    addSupplementAttachment,
    getSupplementAttachments,
    removeSupplementAttachment,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = (): ProjectContextType => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};